<template>
    <div class="transfer-funds-container-wrapper">
        <div class="transfer-funds-container">
            <div class="transfer-funds-header">
                <h5>Transfer funds from selected acrd to EML disbursement account</h5>
            </div>
            <div class="transfer-funds-body">
                <div class="transfer-funds-body-left">
                    <div >
                       Amount
                    </div>
                   <input type="text" v-model="amount" placeholder="$0.00" @input="handleAmountInput" @blur="formatAmountOnBlur">
                </div>
                <div class="transfer-funds-body-left">
                    <div >
                       Description
                    </div>
                   <textarea v-model="reference" placeholder="Type your description here..."></textarea>
                </div>
                <button class="" @click="handleTransfer" :disabled="isTransferring || !canTransfer">Transfer</button>
            </div>
        </div>
        <div class="transfer-history">
            <div class="transfer-history-header">
                <h5>Transfer history</h5>
            </div>
            <div class="transfer-history-body">
                <div class="transfer-history-content">
                    <div v-if="transferList.length === 0" style="text-align: center; padding: 40px; color: #999;">
                        No transfer history
                    </div>
                    <div v-for="(transfer, index) in transferList" :key="index" class="transfer-history-item">
                        <div class="transfer-history-title">
                            <div class="d-flex gap-2">
                                <p class="text-gray ">{{ formatDateDetailed(transfer.created_at || transfer.date) }}</p>
                            </div>
                            <div>
                                <p>{{ transfer.reference || 'N/A' }}</p>
                            </div>
                        </div>
                        <div>
                            <p>{{ formatAmount(transfer.amount) }}</p>
                        </div>
                        <hr v-if="index < transferList.length - 1">
                    </div>
                </div>
                <!-- 分頁控制 -->
                <div v-if="transferList.length > 0" class="pagination-container">
                    <div class="pagination-wrapper">
                        <div @click="changePage(1)" style="cursor: pointer;">
                            <img src="@/assets/images/admin/arrow/prev-end.png" alt="Prev-end" style="width: 25px; height: 25px;" />
                        </div>
                        <div class="pagination-left" @click="changePage(Math.max(1, paginationInfo.current - 1))" style="cursor: pointer;">
                            <img src="@/assets/images/admin/arrow/prev.png" alt="prev" style="width: 25px; height: 25px;" />
                        </div>
                        <span>
                            Page
                            <input type="number" style="width: 60px;" v-model.number="currentPageInput" @change="changePage(currentPageInput)" min="1" :max="paginationInfo.total" />
                            of {{ paginationInfo.total }}
                        </span>
                        <div class="pagination-right" @click="changePage(Math.min(paginationInfo.total, paginationInfo.current + 1))" style="cursor: pointer;">
                            <img src="@/assets/images/admin/arrow/next.png" alt="Next" style="width: 25px; height: 25px;" />
                        </div>
                        <div @click="changePage(paginationInfo.total)" style="cursor: pointer;">
                            <img src="@/assets/images/admin/arrow/next-end.png" alt="Next-end" style="width: 25px; height: 25px;" />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, computed, watch, onMounted } from 'vue';
import { useCardStore } from '@/store/admin/card';
import Swal from 'sweetalert2';
import { formatDateDetailed, formatAmount } from '@/utils/formatters';

const cardStore = useCardStore();

// 表單資料
const amount = ref('');
const reference = ref('');
const isTransferring = ref(false);
const transferList = computed(() => cardStore.transferHistory || []);

// 分頁相關
const currentPageInput = ref(1);

// 計算分頁資訊
const paginationInfo = computed(() => {
    const meta = cardStore.transferHistoryMeta;
    if (!meta) return { current: 1, total: 1, totalItems: 0, start: 0, end: 0 };
    
    // 確保從 meta 中正確獲取數值，處理字符串轉數字
    const current = meta.current_page ? parseInt(meta.current_page) : 1;
    const total = meta.total_pages || meta.last_page ? parseInt(meta.total_pages || meta.last_page) : 1;
    const totalItems = meta.total_items || meta.total ? parseInt(meta.total_items || meta.total) : 0;
    const pageSize = meta.per_page || meta.page_size ? parseInt(meta.per_page || meta.page_size) : 20;
    const start = totalItems > 0 ? (current - 1) * pageSize + 1 : 0;
    const end = Math.min(current * pageSize, totalItems);
    
    return { current, total, totalItems, start, end };
});

// 監聽 paginationInfo 的變化，同步 currentPageInput
watch(() => paginationInfo.value.current, (newCurrent) => {
    if (newCurrent && newCurrent !== currentPageInput.value) {
        currentPageInput.value = newCurrent;
    }
});

// 載入轉帳歷史
const loadTransferHistory = async (page = 1) => {
    const driverId = cardStore.cardInfo?.driver?.id || cardStore.cardInfo?.driver?.driver_id;
    const EAID = cardStore.cardInfo?.EAID;

    if (!driverId || !EAID) {
        return;
    }

    try {
        const history = await cardStore.getTransferHistory({
            driver_id: driverId,
            EAID: EAID,
            eaid: EAID,
            page: page,
        });

        // 後端回傳格式已在 store 中處理，直接使用 cardStore.transferHistory
        // 同步更新分頁輸入框
        if (cardStore.transferHistoryMeta?.current_page) {
            const newPage = parseInt(cardStore.transferHistoryMeta.current_page) || 1;
            if (newPage !== currentPageInput.value) {
                currentPageInput.value = newPage;
            }
        }
    } catch (error) {
        console.error('Failed to load transfer history:', error);
    }
};

// 分頁處理
const changePage = (page) => {
    if (page >= 1 && page <= paginationInfo.value.total) {
        currentPageInput.value = page;
        loadTransferHistory(page);
    }
};

// 計算是否可以轉帳
const canTransfer = computed(() => {
    const numAmount = parseFloat(amount.value.replace(/[^0-9.]/g, '')) || 0;
    return numAmount > 0 && cardStore.cardInfo?.EAID && (cardStore.cardInfo?.driver?.id || cardStore.cardInfo?.driver?.driver_id);
});

// 處理金額輸入（允許輸入多位數字）
const handleAmountInput = (event) => {
    let value = event.target.value.replace(/[^0-9.]/g, '');
    // 只保留一個小數點
    const parts = value.split('.');
    if (parts.length > 2) {
        value = parts[0] + '.' + parts.slice(1).join('');
    }
    // 限制小數點後兩位
    if (parts.length === 2 && parts[1].length > 2) {
        value = parts[0] + '.' + parts[1].substring(0, 2);
    }
    amount.value = value;
};

// 失去焦點時格式化為貨幣格式
const formatAmountOnBlur = () => {
    if (amount.value) {
        const numValue = parseFloat(amount.value.replace(/[^0-9.]/g, ''));
        if (!isNaN(numValue) && numValue > 0) {
            amount.value = `$${numValue.toFixed(2)}`;
        } else {
            amount.value = '';
        }
    }
};

// 處理轉帳
const handleTransfer = async () => {
    if (!canTransfer.value) {
        Swal.fire({
            icon: 'warning',
            title: 'Invalid Input',
            text: 'Please enter a valid amount and ensure card information is loaded.',
            showConfirmButton: false,
            timer: 1500
        });
        return;
    }

    const driverId = cardStore.cardInfo?.driver?.id || cardStore.cardInfo?.driver?.driver_id;
    const EAID = cardStore.cardInfo?.EAID;
    const numAmount = parseFloat(amount.value.replace(/[^0-9.]/g, ''));

    if (!driverId || !EAID) {
        Swal.fire({
            icon: 'error',
            title: 'Error',
            text: 'Driver ID or EAID not found',
            showConfirmButton: false,
            timer: 1500
        });
        return;
    }

    isTransferring.value = true;

    try {
        const payload = {
            driver_id: driverId,
            amount: numAmount,
            EAID: EAID,
            eaid: EAID,
            reference: reference.value || ''
        };

        const result = await cardStore.transferFunds(payload);

        // 顯示成功訊息
        Swal.fire({
            icon: 'success',
            title: 'Transfer Successful',
            text: `Successfully transferred ${formatAmount(numAmount)} to EML disbursement account`,
            showConfirmButton: false,
            timer: 2000
        });

        // 清空表單
        amount.value = '';
        reference.value = '';

        // 重新載入卡片資訊以更新餘額（帶 EAID 以免同 driver 多卡時取錯）
        await cardStore.fetchCardInfo(driverId, EAID != null && EAID !== '' ? { eaid: EAID } : {});

        // 重新載入轉帳歷史（回到第一頁）
        currentPageInput.value = 1;
        await loadTransferHistory(1);
    } catch (error) {
        console.error('Transfer failed:', error);
        Swal.fire({
            icon: 'error',
            title: 'Transfer Failed',
            text: error.message || 'Failed to transfer funds',
            showConfirmButton: false,
            timer: 1500
        });
    } finally {
        isTransferring.value = false;
    }
};

// 初次掛載時載入轉帳歷史
onMounted(() => {
    loadTransferHistory(1);
});

// 當卡片資訊變化時重新載入轉帳歷史
watch(
    () => cardStore.cardInfo,
    () => {
        currentPageInput.value = 1;
        loadTransferHistory(1);
    },
    { deep: true }
);

// 使用工具函數 formatDate, formatTime, formatNumber（已從 @/utils/formatters 導入）
</script>

<style scoped>
.transfer-funds-container-wrapper{
    display:flex;
    gap:20px;
}
.transfer-funds-container{
    background-color:#E6E9EC;
    border-radius:5px;
    padding:20px;
    max-width: 50%;
    height:610px;
}
.transfer-funds-header{
    margin-bottom:20px;
}
.transfer-funds-body-left input{
    border:none;
    padding:8px;
    border-radius:5px;
    background-color:white;
    width: 100%;
    height: 40px;
}
.transfer-funds-body-left textarea {
  border: none;
  padding: 8px;
  border-radius: 5px;
  background-color: white;
  width: 100%;
  min-height: 100px;
  resize: none;
  vertical-align: top;
}
.transfer-funds-body button{
    border:none;
    margin-top:20px;
    padding:10px 40px;
    color:black;
    border-radius:30px;
    background-color:#00C962;
    cursor:pointer;
    transition: background-color 0.2s ease;
}

.transfer-funds-body button:hover:not(:disabled){
    background-color:#00b452;
}

.transfer-funds-body button:disabled{
    background-color:#ccc;
    cursor:not-allowed;
    opacity:0.6;
}
.transfer-history{
    background-color:#E6E9EC;
    border-radius:5px;
    padding:20px;
    width: 25%;
    height:610px;
}
.transfer-history-body{
    background-color:white;
    border-radius:5px;
    padding:20px;
    margin-top:20px;
    height:530px;
    display:flex;
    flex-direction:column;
}

.transfer-history-content{
    flex:1;
    overflow-y:auto;
    min-height:0;
}
.transfer-history-item{
    margin-bottom:15px;
}

.transfer-history-title{
    display:flex;
    justify-content: space-between;
    color:gray;
   
}

.pagination-container{
    display:flex;
    flex-direction: column;
    align-items:center;
    gap:8px;
    
    
}

.pagination-wrapper{
    display:flex;
    gap:6px;
    align-items:center;
    justify-content:center;
    flex-wrap: wrap;
}

.pagination-wrapper input{
    text-align:center;
    border:1px solid #D0D5DD;
    border-radius:5px;
    padding:3px;
    width:56px;
    height:30px;
    font-size:13px;
}

.pagination-wrapper span{
    font-size:14px;
    display:flex;
    align-items:center;
    gap:4px;
    flex-wrap: wrap;
}

.pagination-wrapper img{
    width:20px !important;
    height:20px !important;
}

/* 手機版響應式設計 */
@media (max-width: 768px) {
  .transfer-funds-container-wrapper {
    flex-direction: column;
    gap: 15px;
  }

  .transfer-funds-container {
    max-width: 100%;
    width: 100%;
    height: auto;
    min-height: 400px;
    padding: 15px;
  }

  .transfer-funds-header h5 {
    font-size: 16px;
    line-height: 1.4;
  }

  .transfer-funds-body {
    display: flex;
    flex-direction: column;
    gap: 15px;
  }

  .transfer-funds-body-left {
    width: 100%;
  }

  .transfer-funds-body-left > div {
    font-size: 14px;
    margin-bottom: 8px;
    font-weight: 500;
  }

  .transfer-funds-body button {
    width: 100%;
    padding: 12px 40px;
    font-size: 14px;
    margin-top: 10px;
  }

  .transfer-history {
    width: 100%;
    height: auto;
    min-height: 400px;
    padding: 15px;
  }

  .transfer-history-header h5 {
    font-size: 16px;
  }

  .transfer-history-body {
    height: auto;
    min-height: 350px;
    padding: 15px;
  }

  .transfer-history-title {
    flex-direction: column;
    gap: 8px;
    margin-bottom: 10px;
  }

  .transfer-history-title p {
    font-size: 13px;
    margin: 0;
  }
}

/* 小手機版 (小於 576px) */
@media (max-width: 576px) {
  .transfer-funds-container-wrapper {
    gap: 12px;
  }

  .transfer-funds-container,
  .transfer-history {
    padding: 12px;
  }

  .transfer-funds-header h5 {
    font-size: 14px;
  }

  .transfer-funds-body-left > div {
    font-size: 13px;
  }

  .transfer-funds-body button {
    font-size: 13px;
    padding: 10px 30px;
  }

  .transfer-history-header h5 {
    font-size: 14px;
  }

  .transfer-history-body {
    padding: 12px;
  }

  .transfer-history-title p {
    font-size: 12px;
  }
}
</style>